import useApiClient from "../../useApiClient";
import { useCallback } from "react";
import { withNetworkErrorHandling } from "../../helpers/withNetworkErrorHandling";

const useAdminAPIClient = () => {
  const { get, set, remove } = useApiClient();

  // Check admin status without fetching data
  const getAdminStatus = useCallback(async () => {
    return await withNetworkErrorHandling(() => get("admin?data=status"));
  }, [get]);

  // Full data fetch - backwards compatible
  const getAdminData = useCallback(async () => {
    return await withNetworkErrorHandling(() => get("admin"));
  }, [get]);

  // Granular data fetching methods
  const getUsers = useCallback(async () => {
    return await withNetworkErrorHandling(() => get("admin?data=users"));
  }, [get]);

  const getTasks = useCallback(async () => {
    return await withNetworkErrorHandling(() => get("admin?data=tasks"));
  }, [get]);

  const getSessions = useCallback(async () => {
    return await withNetworkErrorHandling(() => get("admin?data=sessions"));
  }, [get]);

  const getStats = useCallback(async () => {
    return await withNetworkErrorHandling(() => get("admin?data=stats"));
  }, [get]);

  const getFeedback = useCallback(async () => {
    return await withNetworkErrorHandling(() => get("admin?data=feedback"));
  }, [get]);

  const getConversations = useCallback(async () => {
    return await withNetworkErrorHandling(() =>
      get("admin?data=conversations"),
    );
  }, [get]);

  // Get notifications data (feedback + conversations) for badge counts
  const getNotifications = useCallback(async () => {
    return await withNetworkErrorHandling(() =>
      get("admin?data=notifications"),
    );
  }, [get]);

  const sendMessageToUser = useCallback(
    async ({ userId, message, subject }) => {
      return await withNetworkErrorHandling(() =>
        set("admin", { userId, subject, message }),
      );
    },
    [set],
  );

  const sendGlobalMessage = useCallback(
    async ({ subject, message }) => {
      return await withNetworkErrorHandling(() =>
        set("admin", { subject, message }),
      );
    },
    [set],
  );

  const markMessagesAsRead = useCallback(
    async (messageIds) => {
      return await withNetworkErrorHandling(() =>
        set("admin", { action: "markAsRead", messageIds }),
      );
    },
    [set],
  );

  const markFeedbackAsRead = useCallback(
    async (feedbackIds) => {
      return await withNetworkErrorHandling(() =>
        set("admin", { action: "markFeedbackAsRead", feedbackIds }),
      );
    },
    [set],
  );

  const deleteUser = useCallback(
    async (userId) => {
      return await withNetworkErrorHandling(() =>
        remove(`admin/users/${userId}`),
      );
    },
    [remove],
  );

  const toggleFriend = useCallback(
    async (userId) => {
      return await withNetworkErrorHandling(() =>
        set(`admin/users/${userId}/friend`),
      );
    },
    [set],
  );

  return {
    getAdminStatus,
    getAdminData,
    getUsers,
    getTasks,
    getSessions,
    getStats,
    getFeedback,
    getConversations,
    getNotifications,
    sendMessageToUser,
    sendGlobalMessage,
    markMessagesAsRead,
    markFeedbackAsRead,
    deleteUser,
    toggleFriend,
  };
};

export default useAdminAPIClient;
